import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, mergeProps as _mergeProps, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, isRef as _isRef, withCtx as _withCtx, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = { class: "flex flex-col gap-0.5" };
const _hoisted_2 = ["id"];
const _hoisted_3 = ["id"];
const _hoisted_4 = {
    class: "flex flex-col gap-4 py-1",
    "data-testid": "donation-create-modal"
};
const _hoisted_5 = { class: "grid grid-cols-1 sm:grid-cols-2 gap-4" };
const _hoisted_6 = { class: "border border-(--colors-border-base-soft) rounded-xl p-5 flex flex-col" };
const _hoisted_7 = { class: "font-semibold text-(--text-text-heading) dark:text-(--text-text-white-default) mb-5" };
const _hoisted_8 = { class: "border border-(--colors-border-base-soft) rounded-xl p-5 flex flex-col" };
const _hoisted_9 = { class: "font-semibold text-(--text-text-heading) dark:text-(--text-text-white-default) mb-5" };
const _hoisted_10 = { class: "grid grid-cols-1 sm:grid-cols-2 gap-x-4" };
import { computed, reactive, ref, toRef, useId, watch } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useDebounceFn } from "@vueuse/core";
import { toDatePickerFormat } from "@/shared/utils/date-formatting";
import { useDateIsoBinding } from "@/shared/composables";
import { useProfileStore } from "@/modules/settings/stores/useProfileStore";
import { AutoComplete, AutoCompleteAllResultsModal, Button, DatePickerInput, Modal, NumberInput, SelectInput, TextInput, } from "@eudonet/opal";
import { buildAutoCompleteLabels } from "@/shared/composables/autoCompleteLabels";
import { useDonationStore } from "../../stores/useDonationStore";
import { useAppToast } from "@/shared/composables/useAppToast";
import { useDonationOptions } from "../composables/useDonationOptions";
import { useDonorReceiptInformation } from "../composables/useDonorReceiptInformation";
import { describeDonorInformationGap, parseDonorInformationGaps, } from "../../domain/entities/DonorReceiptInformation";
import { getApiErrorCode, getApiErrorResult } from "@/infrastructure/services/ApiErrorService";
import { useDisplayLocale } from "@/shared/composables/useDisplayLocale";
import { useWorkspaceCurrency } from "@/shared/composables/useWorkspaceCurrency";
import { currencySymbol } from "@/shared/utils/workspaceLocaleLabels";
import DonorReceiptInformationNotice from "./DonorReceiptInformationNotice.vue";
import { getContacts } from "@/modules/contact/infrastructure/adapters/contactService";
import { getOrganisations, searchOrganisations, } from "@/modules/organisation/infrastructure/adapters/organisationService";
import { useCampaignOptions } from "../composables/useCampaignOptions";
import { useSubscriptionOptions } from "../composables/useSubscriptionOptions";
import { getDefaultProject, searchProjectsPaged } from "../../infrastructure/api/donationApi";
import { logger } from "@/infrastructure/services/LoggerService";
import CreateOrganisationModal from "@/modules/organisation/presentation/components/CreateOrganisationModal.vue";
import CreateContactModal from "@/modules/contact/presentation/components/CreateContactModal.vue";
// ─── Props / emits ────────────────────────────────────────────────────────────
// The template root is a fragment (the Modal plus its sibling AutoCompleteAllResultsModal
// browsers and nested create sub-modals), so Vue cannot auto-inherit fallthrough attrs and
// warns on any passed attribute. The Modal binds all its props explicitly (no v-bind="$attrs"),
// so disabling inheritance is behaviour-neutral and silences the warning.
const ALL_PROJECTS_PAGE_SIZE = 100;
const ORGS_FETCH_PAGE_SIZE = 100;
// Hard cap on how many we pull/render (see ALL_CONTACTS_FETCH_MAX): the all-results
// modal is not virtualized, so an empty-query browse on a tenant with thousands of
// organisations would otherwise fetch every page and render them all. With a typed
// query the pages are server-filtered, so realistic result sets stay far below the cap.
const ALL_ORGS_FETCH_MAX = 500;
const CONTACTS_FETCH_PAGE_SIZE = 100;
// Hard cap on how many we pull/render: the all-results modal is not virtualized, so
// an empty-query browse on a tenant with thousands of contacts would otherwise fetch
// every page and render them all. With a typed query the pages are server-filtered,
// so realistic result sets stay far below the cap.
const ALL_CONTACTS_FETCH_MAX = 500;
export default /*@__PURE__*/ _defineComponent({
    ...{ inheritAttrs: false },
    __name: 'DonationCreateModal',
    props: /*@__PURE__*/ _mergeModels({
        lockedDonor: {}
    }, {
        "modelValue": { type: Boolean, ...{ default: false } },
        "modelModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(["close", "created"], ["update:modelValue"]),
    setup(__props, { emit: __emit }) {
        const open = _useModel(__props, "modelValue");
        const props = __props;
        const emit = __emit;
        // ─── Dependencies ─────────────────────────────────────────────────────────────
        const router = useRouter();
        const { t, locale } = useI18n();
        // Accessible name/description for the dialog: OPAL Modal renders role="dialog"
        // and forwards fallthrough attrs onto that container, so aria-labelledby /
        // aria-describedby must be wired here (mirrors CreateContactModal /
        // CreateOrganisationModal). Without them the dialog announces with no name.
        const titleId = useId();
        const descriptionId = useId();
        const profileStore = useProfileStore();
        const store = useDonationStore();
        const { toastSuccess, toastError } = useAppToast();
        // The amount is typed and stored in the workspace reference currency. Read again on every open:
        // the modal stays mounted while closed.
        const { currency: workspaceCurrency, isUnavailable: workspaceCurrencyUnavailable, load: loadWorkspaceCurrency, } = useWorkspaceCurrency({ immediate: false });
        watch(open, (isOpen) => {
            if (isOpen)
                void loadWorkspaceCurrency();
        }, { immediate: true });
        const displayLocale = useDisplayLocale();
        const amountPrefix = computed(() => workspaceCurrency.value ? currencySymbol(workspaceCurrency.value, displayLocale.value) : "");
        // OPAL DatePickerInput speaks the user's display format ("dd/mm/yyyy" in fr-FR,
        // "mm/dd/yyyy" by default) in both directions. Keep `form.donationDate` in the
        // wire format (ISO yyyy-MM-dd) and bridge via `useDateIsoBinding` — otherwise
        // the picker emits "05/27/2026", the submit-time `${form.donationDate}T12:00:00Z`
        // produces an invalid instant, and the BFF returns 400 with
        // "JSON value could not be converted to System.DateTime".
        const dateFormat = computed(() => toDatePickerFormat(profileStore.profile?.dateFormat, locale.value));
        // All select options come from useDonationOptions, which itself reads the BFF
        // catalog (`store.catalogs`) for catalog-backed enums. Single source of truth.
        const { donorTypeOptions, donationSourceOptions, donationTypeOptions, paymentMethodOptions, statusOptions, donationFormOptions, donationNatureOptions, } = useDonationOptions();
        // At creation the backend only accepts Recorded (immediate), Promesse (pledge) or Validated
        // (back-office entry of an already-received donation). The full transaction-status catalog
        // (incl. terminal Rejected/Canceled/Refunded) is only valid for status transitions on an
        // existing donation, so it must not be offered here (would 400 on submit — FE↔BE alignment).
        const CREATION_STATUSES = ["Recorded", "Promesse", "Validated"];
        const creationStatusOptions = computed(() => statusOptions.value.filter((option) => CREATION_STATUSES.includes(option.value)));
        // "See all" shows only when more than the dropdown's 5-row cap exist.
        function seeAllLabel(count) {
            return count > 5 ? t("finance.donations.create.seeAllResults", { count }) : undefined;
        }
        // ─── Contact picker — browse buffer + server-side search per keystroke ─────────
        // The initial load only fetches the first 50 rows (browse-all buffer); a contact
        // outside that buffer would be unreachable through an in-memory filter on a
        // large tenant, so typed queries go to GET /contacts?search= instead. A
        // monotonic token discards a slow earlier response that would otherwise
        // overwrite a newer one (mirrors the project picker's onProjectSearch).
        const contacts = ref([]);
        const contactSuggestions = ref([]);
        const contactsLoading = ref(false);
        // Unfiltered (or org-scoped) DB total — drives the "Voir tous les résultats (XX)"
        // footer for the empty-query browse-all case.
        const contactTotalCount = ref(0);
        // Server-side total for the current typed query (see `contactSeeAllCount`).
        const contactSearchTotal = ref(0);
        function contactToOption(c) {
            const name = `${c.firstName} ${c.lastName}`.trim() || c.id;
            return { value: c.id, label: name };
        }
        // Tracks which organisationId the contacts list was loaded for (undefined = no org filter).
        // Used to avoid redundant reloads and to detect when the org changes.
        let contactsLoadedForOrgId = undefined;
        let contactsLoaded = false;
        async function loadContacts(organisationId) {
            if (contactsLoaded && contactsLoadedForOrgId === organisationId)
                return;
            // A fresh buffer context (donor-type/org switch, post-create refresh) resets the
            // search state: any in-flight search targets the previous context and its result
            // must not overwrite the new buffer, and the footer count reverts to browse mode.
            contactSearchSeq++;
            contactSearchQuery.value = "";
            contactsLoading.value = true;
            try {
                const res = await getContacts({ page: 1, pageSize: 50, organisationId });
                contacts.value = res.result;
                contactSuggestions.value = res.result.map(contactToOption);
                contactTotalCount.value = res.totalItems;
                contactsLoaded = true;
                contactsLoadedForOrgId = organisationId;
            }
            catch {
                toastError("contact.loadError");
            }
            finally {
                contactsLoading.value = false;
            }
        }
        function contactSearchOrgScope() {
            return form.donorType === "Organization" ? form.organizationId || undefined : undefined;
        }
        // Live search term, mirrored synchronously from @search so the footer's count can
        // be computed outside the slot and so a query superseded during the debounce
        // window is detected before its fetch fires.
        const contactSearchQuery = ref("");
        let contactSearchSeq = 0;
        const debouncedContactFetch = useDebounceFn(async (query) => {
            // Superseded while debouncing (newer keystroke, or query emptied) → the
            // pending fetch for the stale term must not fire at all.
            if (query !== contactSearchQuery.value)
                return;
            const requestId = ++contactSearchSeq;
            contactsLoading.value = true;
            try {
                const res = await getContacts({
                    search: query,
                    organisationId: contactSearchOrgScope(),
                    page: 1,
                    pageSize: 50,
                });
                if (requestId !== contactSearchSeq)
                    return;
                contactSuggestions.value = res.result.map(contactToOption);
                contactSearchTotal.value = res.totalItems;
            }
            catch {
                if (requestId === contactSearchSeq)
                    toastError("contact.loadError");
            }
            finally {
                if (requestId === contactSearchSeq)
                    contactsLoading.value = false;
            }
        }, 300);
        function onContactSearch(query) {
            const q = query.trim();
            contactSearchQuery.value = q;
            if (!q) {
                // Collapsed query: nothing left to search, so restore the browse buffer
                // synchronously (no debounce) and invalidate any in-flight response.
                contactSearchSeq++;
                contactsLoading.value = false;
                contactSuggestions.value = contacts.value.map(contactToOption);
                return;
            }
            debouncedContactFetch(q);
        }
        // Footer count: server total for the current query, DB total when browsing.
        const contactSeeAllCount = computed(() => contactSearchQuery.value ? contactSearchTotal.value : contactTotalCount.value);
        const contactSeeAllLabel = computed(() => seeAllLabel(contactSeeAllCount.value));
        const contactCreateLabel = (query) => t("finance.donations.create.createAsNewContact", { name: query.trim() });
        // ─── Organisation picker — browse buffer + server-side search per keystroke ────
        // The initial load only fetches the first 50 rows (browse-all buffer); an
        // organisation outside that buffer would be unreachable through an in-memory
        // filter on a large tenant, so typed queries go to GET /organisations?search=
        // (case-insensitive substring on the legal name) instead, with the same
        // monotonic out-of-order guard as the contact picker.
        const organisations = ref([]);
        const organisationSuggestions = ref([]);
        const organisationsLoading = ref(false);
        // Unfiltered DB total — drives the "Voir tous les résultats (XX)" footer for the
        // empty-query browse-all case.
        const organisationTotalCount = ref(0);
        // Server-side total for the current typed query (see `organisationSeeAllCount`).
        const organisationSearchTotal = ref(0);
        let organisationsLoaded = false;
        function organisationToOption(o) {
            const name = o.mainInformation.legalName?.trim() || o.mainInformation.acronym?.trim() || o.id;
            return { value: o.id, label: name };
        }
        async function loadOrganisations() {
            if (organisationsLoaded)
                return;
            // A fresh buffer (post-create refresh) resets the search state: any in-flight
            // search result must not overwrite the new buffer, and the footer count
            // reverts to browse mode.
            organisationSearchSeq++;
            organisationSearchQuery.value = "";
            organisationsLoading.value = true;
            try {
                const res = await getOrganisations({ page: 1, pageSize: 50 });
                organisations.value = res.result;
                organisationSuggestions.value = res.result.map(organisationToOption);
                organisationTotalCount.value = res.totalItems;
                organisationsLoaded = true;
            }
            catch {
                toastError("organisationList.loadError");
            }
            finally {
                organisationsLoading.value = false;
            }
        }
        // Live search term, mirrored synchronously from @search so the footer's count can
        // be computed outside the slot and so a query superseded during the debounce
        // window is detected before its fetch fires.
        const organisationSearchQuery = ref("");
        let organisationSearchSeq = 0;
        const debouncedOrganisationFetch = useDebounceFn(async (query) => {
            // Superseded while debouncing (newer keystroke, or query emptied) → the
            // pending fetch for the stale term must not fire at all.
            if (query !== organisationSearchQuery.value)
                return;
            const requestId = ++organisationSearchSeq;
            organisationsLoading.value = true;
            try {
                const res = await searchOrganisations({ search: query, page: 1, pageSize: 50 });
                if (requestId !== organisationSearchSeq)
                    return;
                organisationSuggestions.value = res.result.map(organisationToOption);
                organisationSearchTotal.value = res.totalItems;
            }
            catch {
                if (requestId === organisationSearchSeq)
                    toastError("organisationList.loadError");
            }
            finally {
                if (requestId === organisationSearchSeq)
                    organisationsLoading.value = false;
            }
        }, 300);
        function onOrganisationSearch(query) {
            const q = query.trim();
            organisationSearchQuery.value = q;
            if (!q) {
                // Collapsed query: nothing left to search, so restore the browse buffer
                // synchronously (no debounce) and invalidate any in-flight response.
                organisationSearchSeq++;
                organisationsLoading.value = false;
                organisationSuggestions.value = organisations.value.map(organisationToOption);
                return;
            }
            debouncedOrganisationFetch(q);
        }
        // Footer count: server total for the current query, DB total when browsing.
        const organisationSeeAllCount = computed(() => organisationSearchQuery.value ? organisationSearchTotal.value : organisationTotalCount.value);
        const organisationSeeAllLabel = computed(() => seeAllLabel(organisationSeeAllCount.value));
        const organisationCreateLabel = (query) => t("finance.donations.create.createAsNewOrganisation", { name: query.trim() });
        // ─── Campaign picker — shared data source (useCampaignOptions) ─────────────────
        // The composable owns the browse buffer, the debounced server-side
        // GET /campaigns?search= with its out-of-order guard, and the query-aware footer
        // count. `status: null` keeps this modal's historical behaviour: every campaign
        // status is offered, not the composable's Active-only default.
        const { campaigns, options: campaignSuggestions, loading: campaignsLoading, seeAllCount: campaignSeeAllCount, toOption: campaignToOption, load: loadCampaigns, search: onCampaignSearch, noteSelected: noteCampaignSelected, fetchAllItems: fetchAllCampaignItems, } = useCampaignOptions({ status: null });
        const campaignSeeAllLabel = computed(() => seeAllLabel(campaignSeeAllCount.value));
        // ─── Subscription picker — shared with the donation detail via useSubscriptionOptions ───
        // Loaded once across all donors (no donorId scope here: at creation the donor may not yet have an
        // OPDON donor record). `subscriptions` feeds the submit-time frequency lookup.
        const { subscriptions, options: subscriptionSuggestions, loading: subscriptionsLoading, load: loadSubscriptions, search: searchSubscriptions, } = useSubscriptionOptions();
        // ─── Affectation picker — oproj GET /portal/projects/search (server-side) ──────
        // Unlike the other pickers (client-side filter over a pre-fetched buffer), the
        // project list lives in OPROJ and is searched server-side per keystroke. A
        // monotonic token discards a slow earlier response that would otherwise
        // overwrite a newer one. Mirrors DonationTabAllocations' project picker.
        const projectSuggestions = ref([]);
        const projectsLoading = ref(false);
        // OPROJ's total match count for the current query — drives the "See all
        // results (XX)" footer button (only shown when more than the dropdown's 5-row
        // cap exist).
        const projectTotalCount = ref(0);
        const projectSeeAllLabel = computed(() => seeAllLabel(projectTotalCount.value));
        let projectSearchSeq = 0;
        function projectToOption(p) {
            return { value: p.projectId, label: p.name || p.projectId, description: p.code };
        }
        async function onProjectSearch(query) {
            const requestId = ++projectSearchSeq;
            projectsLoading.value = true;
            try {
                const { items, totalItems } = await searchProjectsPaged(query.trim());
                if (requestId === projectSearchSeq) {
                    projectSuggestions.value = items.map(projectToOption);
                    projectTotalCount.value = totalItems;
                }
            }
            catch (err) {
                logger.error("DonationCreateModal: project search failed", err);
                if (requestId === projectSearchSeq) {
                    projectSuggestions.value = [];
                    projectTotalCount.value = 0;
                }
            }
            finally {
                if (requestId === projectSearchSeq)
                    projectsLoading.value = false;
            }
        }
        // ─── Nested "See all project results" sub-modal ──────────────────────────────
        // Opened from the Affectation picker's dropdown footer when OPROJ reports more
        // matches than the dropdown's 5-row cap can show. Unlike the org "see all"
        // browser (which fetches everything and filters client-side), project search is
        // server-side, so we re-query OPROJ with a larger pageSize for the same term.
        const showAllProjectsModal = ref(false);
        const allProjectsQuery = ref("");
        const allProjectsOptions = ref([]);
        const allProjectsLoading = ref(false);
        const allProjectsError = ref(false);
        async function openAllProjectsModal(query) {
            allProjectsQuery.value = query;
            showAllProjectsModal.value = true;
            allProjectsError.value = false;
            allProjectsLoading.value = true;
            try {
                const { items } = await searchProjectsPaged(query.trim(), 1, ALL_PROJECTS_PAGE_SIZE);
                allProjectsOptions.value = items.map(projectToOption);
            }
            catch (err) {
                logger.error("DonationCreateModal: project see-all fetch failed", err);
                allProjectsOptions.value = [];
                allProjectsError.value = true;
            }
            finally {
                allProjectsLoading.value = false;
            }
        }
        function onAllProjectsConfirm(option) {
            form.affectation = option.value;
        }
        // ─── Nested "Create new contact" sub-modal ───────────────────────────────────
        // Opened from the Contact picker's dropdown footer. CreateContactModal normally
        // redirects to the new contact's detail page; we opt out (`redirect-on-create
        // ="false"`) so the user stays on the donation form. On `created`, refresh the
        // contact list and pre-select the new contact so the donation can be submitted
        // immediately.
        const showCreateContactModal = ref(false);
        // Lazy-mount: CreateContactModal pulls in dedup/preview composables and lookups
        // in its own setup. Keep it unmounted until first opened so that machinery
        // doesn't run on every donation-modal open.
        const createContactModalMounted = ref(false);
        watch(showCreateContactModal, (open) => {
            if (open)
                createContactModalMounted.value = true;
        });
        function openCreateContact() {
            showCreateContactModal.value = true;
        }
        // Org context handed to the nested CreateContactModal. For an Organisation donation the
        // new contact MUST be linked to the selected org (initial-organisation-id forces the
        // Professional type and makes OPHUB create the default contact↔org relation). Without it
        // the contact is created unlinked, and onContactCreated's org-filtered reload never
        // surfaces it — the picker stays empty and the just-created contact can't be selected.
        // Individual donations have no org context, so the props resolve to undefined.
        const contactModalOrgId = computed(() => form.donorType === "Organization" && form.organizationId ? form.organizationId : undefined);
        const contactModalOrgLabel = computed(() => {
            const id = contactModalOrgId.value;
            if (!id)
                return undefined;
            const fromBuffer = organisations.value.find((o) => o.id === id);
            if (fromBuffer)
                return organisationToOption(fromBuffer).label;
            return organisationSuggestions.value.find((o) => o.value === id)?.label;
        });
        async function onContactCreated(newId, _relationTitle, name, organisation) {
            showCreateContactModal.value = false;
            // Organisation donation whose Raison Sociale was still empty: adopt the org the user
            // linked the referent contact to, so the donor org stays consistent with the contact.
            // Seed the picker label first (mirrors onOrganisationCreated) so the AutoComplete renders
            // the name instead of the raw GUID, then loadContacts below is correctly org-filtered.
            if (form.donorType === "Organization" && !form.organizationId && organisation?.id) {
                if (!organisationSuggestions.value.some((o) => o.value === organisation.id)) {
                    organisationSuggestions.value = [
                        { value: organisation.id, label: organisation.label?.trim() || organisation.id },
                        ...organisationSuggestions.value,
                    ];
                }
                form.organizationId = organisation.id;
            }
            contactsLoaded = false;
            const organisationId = form.donorType === 'Organization' ? form.organizationId || undefined : undefined;
            await loadContacts(organisationId);
            // The (org-filtered) reload only fetches the first 50 contacts; on an org with more than
            // that the freshly-created contact can fall outside the buffer, leaving the picker no
            // matching option (raw GUID) and handleSubmit no name to forward. Seed both the picker
            // options and the contacts buffer with the emitted name so the label resolves and the
            // donor/referent display name survives regardless (mirrors onAllContactsConfirm).
            if (name && !contacts.value.some((c) => c.id === newId)) {
                contacts.value = [
                    {
                        id: newId,
                        salutation: "",
                        firstName: name.firstName,
                        lastName: name.lastName,
                        status: "",
                        addresses: [],
                        communicationChannels: [],
                        organisationRoles: [],
                        categories: [],
                        tags: [],
                    },
                    ...contacts.value,
                ];
            }
            if (!contactSuggestions.value.some((o) => o.value === newId)) {
                const label = name ? `${name.firstName} ${name.lastName}`.trim() || newId : newId;
                contactSuggestions.value = [{ value: newId, label }, ...contactSuggestions.value];
            }
            form.contact = newId;
        }
        // ─── Nested "Create new organisation" sub-modal ───────────────────────────────
        // Opened from the Raison Sociale picker's dropdown footer when the user types a
        // new name. After CreateOrganisationModal emits `created`, we refresh the org
        // list and pre-select the new entity in the donation form.
        const showCreateOrgModal = ref(false);
        // Lazy-mount: CreateOrganisationModal stays unmounted until first opened so
        // its initialisation (lookups, etc.) doesn't fire when the donation modal
        // is opened just to create a contact/individual donation.
        const createOrgModalMounted = ref(false);
        watch(showCreateOrgModal, (open) => {
            if (open)
                createOrgModalMounted.value = true;
        });
        // The term the user typed in the Raison Sociale picker, forwarded to
        // CreateOrganisationModal's `initial-name` so its Company name field is
        // prefilled instead of starting empty (mirrors ContactAddressFormModal /
        // OrganisationTabDetails — OP-6103).
        const createOrgInitialName = ref("");
        function openCreateOrg(searchQuery = "") {
            createOrgInitialName.value = searchQuery.trim();
            showCreateOrgModal.value = true;
        }
        async function onOrganisationCreated(newId, name) {
            showCreateOrgModal.value = false;
            organisationsLoaded = false;
            await loadOrganisations();
            // loadOrganisations() only fetches the first 50 orgs; on a tenant with more than
            // that the freshly-created org can fall outside the buffer (or lag behind in the
            // list read-model), leaving the AutoComplete no matching option and rendering the
            // raw GUID instead of the name. Seed the picker with the emitted name so the label
            // resolves regardless (mirrors onAllContactsConfirm / onAllCampaignsConfirm).
            if (!organisationSuggestions.value.some((o) => o.value === newId)) {
                organisationSuggestions.value = [
                    { value: newId, label: name.trim() || newId },
                    ...organisationSuggestions.value,
                ];
            }
            form.organizationId = newId;
        }
        // ─── Nested "See all organisation results" sub-modal ─────────────────────────
        // The search term the user had typed goes to the SERVER (same GET /organisations
        // ?search= as the dropdown), so the listed matches are not limited by any
        // client-side buffer — the pages fetched here are already filtered.
        // Organisations API enforces PageSize 1-100 (same constraint as contacts); fetch
        // pages to surface matches beyond the first page.
        const showAllOrgsModal = ref(false);
        const allOrgsQuery = ref("");
        const allOrgsList = ref([]);
        const allOrgsLoading = ref(false);
        const allOrgsError = ref(false);
        const allOrgsOptions = computed(() => allOrgsList.value.map(organisationToOption));
        async function openAllOrgsModal(query) {
            allOrgsQuery.value = query;
            showAllOrgsModal.value = true;
            allOrgsError.value = false;
            allOrgsLoading.value = true;
            try {
                const all = [];
                let page = 1;
                let total = Infinity;
                const search = query.trim() || undefined;
                while (all.length < total && all.length < ALL_ORGS_FETCH_MAX) {
                    // An empty query must keep using the plain list endpoint: searchOrganisations
                    // without a `search` term routes to the saved-filter search, not the browse list.
                    const res = search
                        ? await searchOrganisations({ search, page, pageSize: ORGS_FETCH_PAGE_SIZE })
                        : await getOrganisations({ page, pageSize: ORGS_FETCH_PAGE_SIZE });
                    all.push(...res.result);
                    total = res.totalItems;
                    if (res.result.length === 0)
                        break;
                    page++;
                }
                allOrgsList.value = all.slice(0, ALL_ORGS_FETCH_MAX);
            }
            catch {
                allOrgsList.value = [];
                allOrgsError.value = true;
            }
            finally {
                allOrgsLoading.value = false;
            }
        }
        function onAllOrgsConfirm(option) {
            form.organizationId = option.value;
            // handleSubmit and contactModalOrgLabel resolve the org's display name from
            // `organisations.value` first; add the chosen org so a selection beyond the
            // dropdown's first-50 buffer keeps its name (mirrors onAllContactsConfirm).
            const selected = allOrgsList.value.find((o) => o.id === option.value);
            if (selected && !organisations.value.some((o) => o.id === selected.id)) {
                organisations.value = [selected, ...organisations.value];
            }
            // Seed the picker so the AutoComplete renders the org name, not the raw id.
            organisationSuggestions.value = [
                option,
                ...organisationSuggestions.value.filter((o) => o.value !== option.value),
            ];
        }
        // ─── Nested "See all contact results" sub-modal ───────────────────────────────
        // The search term the user had typed goes to the SERVER (same GET /contacts?search=
        // as the dropdown), so the listed matches are not limited by any client-side
        // buffer — the pages fetched here are already filtered.
        // Contacts API enforces PageSize 1-100; fetch pages to surface matches beyond the
        // first page.
        const showAllContactsModal = ref(false);
        const allContactsQuery = ref("");
        const allContactsList = ref([]);
        const allContactsLoading = ref(false);
        const allContactsError = ref(false);
        const allContactsOptions = computed(() => allContactsList.value.map(contactToOption));
        async function openAllContactsModal(query) {
            allContactsQuery.value = query;
            showAllContactsModal.value = true;
            allContactsError.value = false;
            allContactsLoading.value = true;
            try {
                const all = [];
                let page = 1;
                let total = Infinity;
                const search = query.trim() || undefined;
                const organisationId = contactSearchOrgScope();
                while (all.length < total && all.length < ALL_CONTACTS_FETCH_MAX) {
                    const res = await getContacts({ page, pageSize: CONTACTS_FETCH_PAGE_SIZE, organisationId, search });
                    all.push(...res.result);
                    total = res.totalItems;
                    if (res.result.length === 0)
                        break;
                    page++;
                }
                allContactsList.value = all.slice(0, ALL_CONTACTS_FETCH_MAX);
            }
            catch {
                allContactsList.value = [];
                allContactsError.value = true;
            }
            finally {
                allContactsLoading.value = false;
            }
        }
        function onAllContactsConfirm(option) {
            form.contact = option.value;
            // handleSubmit derives donorDisplayName/contactDisplayName from `contacts.value`;
            // add the chosen contact so a selection beyond the dropdown's first-50 buffer keeps
            // its display-name fallback instead of resolving to undefined at submit.
            const selected = allContactsList.value.find((c) => c.id === option.value);
            if (selected && !contacts.value.some((c) => c.id === selected.id)) {
                contacts.value = [selected, ...contacts.value];
            }
            // Seed the picker so the AutoComplete renders the contact name, not the raw id.
            contactSuggestions.value = [option, ...contactSuggestions.value.filter((o) => o.value !== option.value)];
        }
        // ─── Nested "See all campaign results" sub-modal ──────────────────────────────
        // The search term the user had typed goes to the SERVER (same GET /campaigns?search=
        // as the dropdown, via the composable's capped paged fetch), so the listed matches
        // are not limited by any client-side buffer.
        const showAllCampaignsModal = ref(false);
        const allCampaignsQuery = ref("");
        const allCampaignsList = ref([]);
        // Server total for the query — the title's count. The fetched list is capped, so
        // its length can undercount what the footer button advertised.
        const allCampaignsTotal = ref(0);
        const allCampaignsLoading = ref(false);
        const allCampaignsError = ref(false);
        const allCampaignsOptions = computed(() => allCampaignsList.value.map(campaignToOption));
        async function openAllCampaignsModal(query) {
            allCampaignsQuery.value = query;
            showAllCampaignsModal.value = true;
            allCampaignsError.value = false;
            allCampaignsLoading.value = true;
            try {
                const { items, totalCount } = await fetchAllCampaignItems(query);
                allCampaignsList.value = items;
                allCampaignsTotal.value = totalCount;
            }
            catch {
                allCampaignsList.value = [];
                allCampaignsTotal.value = 0;
                allCampaignsError.value = true;
            }
            finally {
                allCampaignsLoading.value = false;
            }
        }
        function onAllCampaignsConfirm(option) {
            form.campaign = option.value;
            // handleSubmit resolves campaignId/campaignName from `campaigns.value`; add the
            // chosen campaign so a selection beyond the dropdown's first-50 buffer survives
            // submit instead of being silently dropped.
            const selected = allCampaignsList.value.find((c) => c.id === option.value);
            if (selected && !campaigns.value.some((c) => c.id === selected.id)) {
                campaigns.value = [selected, ...campaigns.value];
            }
            // Seed the picker so the AutoComplete renders the campaign name, not the raw id.
            campaignSuggestions.value = [option, ...campaignSuggestions.value.filter((o) => o.value !== option.value)];
        }
        // ─── Form state ───────────────────────────────────────────────────────────────
        function todayLocalIsoDate() {
            const d = new Date();
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, "0");
            const day = String(d.getDate()).padStart(2, "0");
            return `${y}-${m}-${day}`;
        }
        // Modale Don V2 spec defaults — pre-fill the most common values so the user
        // only has to fill the donor / amount / campaign in the happy path.
        const FORM_DEFAULTS = {
            donorType: "Individual",
            contact: "",
            organizationId: "",
            amountStr: "",
            paymentMethod: "Cheque", // Chèque = default when nature = Cash
            donationNature: "Cash", // Numéraire (par défaut)
            donationDate: "",
            initialStatus: "Recorded", // Enregistré (par défaut)
            donationType: "OneOff", // Ponctuel (par défaut)
            subscriptionId: "",
            chequeNumber: "",
            externalTransactionId: "",
            affectation: "",
            campaign: "",
            donationForm: "ManualDonation", // Déclaration de don manuel (par défaut)
            donationSource: "Mail", // Courrier (par défaut)
        };
        const form = reactive({
            ...FORM_DEFAULTS,
            donationDate: todayLocalIsoDate(),
        });
        // OP-7954: an Organisation donation checks the org AND its referent contact; an Individual one
        // checks the contact, which IS the donor.
        const receiptInformation = useDonorReceiptInformation({
            contactId: computed(() => form.contact || undefined),
            organizationId: computed(() => form.donorType === "Organization" ? form.organizationId || undefined : undefined),
            enabled: open,
        });
        // ─── Locked-donor context (fiche entry points, OP-6139) ───────────────────────
        // When opened from a contact/organisation fiche the donor side is pre-bound and
        // disabled. We seed the picker option list directly (label = displayName) so the
        // disabled AutoComplete renders the name without a fetch, and we DO NOT call the
        // generic loaders for the locked side (which would overwrite the seeded option).
        function applyLockedDonor(locked) {
            if (locked.donorType === "Organization") {
                // Org is the donor (locked); the contact picker stays active as the required
                // referent contact, scoped to the org. We load that scoped list explicitly
                // rather than relying on the organizationId watcher: this runs from the
                // `{ immediate: true }` open-watcher during setup, BEFORE that watcher is
                // registered, so it would never fire for this initial assignment.
                form.donorType = "Organization";
                organisationSuggestions.value = [{ value: locked.organisationId, label: locked.displayName }];
                organisationTotalCount.value = 1;
                organisationsLoaded = true;
                form.organizationId = locked.organisationId;
                contactsLoaded = false;
                loadContacts(locked.organisationId);
            }
            else {
                // Contact is the donor (locked). donorType is already 'Individual' (default),
                // so no donor-type watcher fires to clear the contact.
                contactSuggestions.value = [{ value: locked.contactId, label: locked.displayName }];
                contactTotalCount.value = 1;
                contactsLoaded = true;
                form.contact = locked.contactId;
            }
        }
        // Lazy: fetch all picker datasets the first time the modal opens.
        // `immediate: true` so the modal also loads its data when it is already open on
        // first mount — e.g. DonationListView opens it via `?action=new`, mounting the
        // component with `open` already true. A changes-only watcher would never fire on
        // that initial render, leaving every picker empty until the user closes and
        // reopens (mirrors SubscriptionCreateModal).
        watch(open, (isOpen) => {
            if (!isOpen)
                return;
            store.loadCatalogs().catch(() => toastError("finance.donations.catalogs.loadError"));
            loadCampaigns();
            loadSubscriptions();
            if (props.lockedDonor) {
                applyLockedDonor(props.lockedDonor);
            }
            else {
                loadContacts();
                loadOrganisations();
            }
        }, { immediate: true });
        // Display-format proxy for the DatePickerInput. `form.donationDate` stays ISO
        // (yyyy-MM-dd) so the submit-time `${form.donationDate}T12:00:00Z` produces a
        // valid instant.
        const donationDateDisplay = useDateIsoBinding(toRef(form, "donationDate"), computed(() => profileStore.profile?.dateFormat), locale);
        // Modale Don V2: payment / cheque / transaction-reference fields are only valid
        // when nature = Numéraire (Cash). Clear them whenever the user switches away —
        // this also clears any pending validation errors on those fields. When switching
        // back to Cash, restore the spec defaults so the form behaves like a fresh open.
        watch(() => form.donationNature, (nature, previousNature) => {
            if (nature !== "Cash") {
                form.paymentMethod = "";
                form.chequeNumber = "";
                form.externalTransactionId = "";
                errors.paymentMethod = "";
                errors.chequeNumber = "";
            }
            else if (previousNature !== "Cash") {
                // Switching back to Cash → restore the spec default ("Chèque").
                form.paymentMethod = FORM_DEFAULTS.paymentMethod;
            }
        });
        // When the donor type changes, the contact list must reflect the new context: switching
        // to Organisation loads contacts filtered to the already-selected org (if any), or
        // unfiltered when no org is selected yet; switching back to Individual reloads the
        // unfiltered list. We pass the orgId here because the org watcher below only fires on
        // value *changes* — it won't re-fire if organizationId is already set when the type switches.
        watch(() => form.donorType, (newType, oldType) => {
            if (newType === oldType)
                return;
            form.contact = '';
            contactsLoaded = false;
            contactsLoadedForOrgId = undefined;
            const orgId = newType === 'Organization' ? form.organizationId || undefined : undefined;
            loadContacts(orgId);
        });
        // When the selected organisation changes (only for Organisation donations), reload the
        // contact dropdown filtered to that organisation and reset any previously selected contact.
        watch(() => form.organizationId, (newOrgId, oldOrgId) => {
            if (form.donorType !== 'Organization' || newOrgId === oldOrgId)
                return;
            form.contact = '';
            contactsLoaded = false;
            loadContacts(newOrgId || undefined);
        });
        // ─── Affectation: default pre-selection + mandatory-field handling (OP-5841) ────
        // Pre-select the tenant's default project as the donation's allocation when the
        // modal opens (AC1). The field is mandatory; if no default is configured the
        // field stays empty and the user must pick one (AC2/AC3). A monotonic token
        // avoids clobbering a value the user chose while the request was in flight, and
        // a lookup failure never blocks the form (R47).
        let defaultProjectSeq = 0;
        watch(open, async (isOpen) => {
            if (!isOpen)
                return;
            const requestId = ++defaultProjectSeq;
            try {
                const def = await getDefaultProject();
                if (requestId !== defaultProjectSeq || !def || form.affectation.trim())
                    return;
                form.affectation = def.projectId;
                // Seed the picker so the AutoComplete renders the project name, not the raw id.
                projectSuggestions.value = [projectToOption(def)];
            }
            catch (err) {
                logger.error("DonationCreateModal: default project lookup failed", err);
            }
        }, { immediate: true });
        // True once the allocation field has ever held a non-empty value (pre-fill or
        // user selection). Gating the "show error on clear" logic on this flag avoids
        // showing the error on initial render when no default project is configured.
        // @blur alone is insufficient because OPAL AutoComplete does not emit it when
        // the clear (×) button is clicked — focus stays within the component.
        const affectationEverHadValue = ref(false);
        // Clear the error as soon as a project is chosen; show it when the value is
        // cleared after having been set at least once (pre-fill or manual selection).
        watch(() => form.affectation, (value) => {
            if (value.trim()) {
                affectationEverHadValue.value = true;
                errors.affectation = "";
            }
            else if (affectationEverHadValue.value) {
                errors.affectation = t("finance.donations.validation.allocationRequired");
            }
        });
        // Blur validation (AC4): empty + lose focus → "Allocation is required."
        // Handles the case where the field was always empty and the user tabs through it.
        function onAffectationBlur() {
            errors.affectation = form.affectation.trim()
                ? ""
                : t("finance.donations.validation.allocationRequired");
        }
        // Clear the mandatory-contact error as soon as a contact is chosen.
        watch(() => form.contact, (value) => {
            if (value.trim())
                errors.contact = "";
        });
        // Blur validation (OP-5843): empty contact + lose focus → "Contact is required."
        // The contact is mandatory for both donor types (the org's "Contact référent" for an
        // Organisation donation, the donor for an Individual one).
        function onContactBlur() {
            errors.contact = form.contact.trim()
                ? ""
                : t("finance.donations.validation.contactRequired");
        }
        // ─── Blur validation for remaining mandatory fields (OP-5863) ─────────────────
        watch(() => form.organizationId, (value) => {
            if (value.trim())
                errors.organizationId = "";
        });
        function onOrganizationBlur() {
            if (form.donorType !== "Organization")
                return;
            errors.organizationId = form.organizationId.trim()
                ? ""
                : t("finance.donations.validation.donorRequired");
        }
        watch(() => form.campaign, (value) => {
            if (!value.trim())
                return;
            errors.campaign = "";
            // handleSubmit resolves campaignId/campaignName from the composable's
            // `campaigns` buffer; a selection made from server-searched suggestions lives
            // outside it, so seed it or the submit silently drops the campaign (mirrors
            // onAllCampaignsConfirm).
            noteCampaignSelected(value);
        });
        function onCampaignBlur() {
            errors.campaign = form.campaign.trim()
                ? ""
                : t("finance.donations.validation.campaignRequired");
        }
        watch(() => form.amountStr, (value) => {
            const n = parseFloat(value);
            if (value && !isNaN(n) && n > 0)
                errors.amount = "";
        });
        function onAmountBlur() {
            const n = parseFloat(form.amountStr);
            errors.amount =
                form.amountStr && !isNaN(n) && n > 0
                    ? ""
                    : t("finance.donations.validation.amountPositive");
        }
        watch(() => form.donationNature, (value) => {
            if (value)
                errors.donationNature = "";
        });
        function onDonationNatureBlur() {
            errors.donationNature = form.donationNature
                ? ""
                : t("finance.donations.validation.natureRequired");
        }
        watch(() => form.paymentMethod, (value) => {
            if (value)
                errors.paymentMethod = "";
        });
        function onPaymentMethodBlur() {
            if (form.donationNature !== "Cash")
                return;
            errors.paymentMethod = form.paymentMethod
                ? ""
                : t("finance.donations.validation.paymentMethodRequired");
        }
        watch(() => form.chequeNumber, (value) => {
            if (value.trim())
                errors.chequeNumber = "";
        });
        function onChequeNumberBlur() {
            if (form.donationNature !== "Cash" || form.paymentMethod !== "Cheque")
                return;
            errors.chequeNumber = form.chequeNumber.trim()
                ? ""
                : t("finance.donations.validation.chequeNumberRequired");
        }
        // DatePickerInput does not emit blur, so validation is driven by the value watcher:
        // clear error when a date is chosen, show error when the field is cleared after having
        // had a value (oldValue guard prevents the error appearing on initial mount).
        watch(() => form.donationDate, (value, oldValue) => {
            if (value) {
                errors.donationDate = "";
            }
            else if (oldValue) {
                errors.donationDate = t("finance.donations.validation.dateRequired");
            }
        });
        watch(() => form.initialStatus, (value) => {
            if (value)
                errors.initialStatus = "";
        });
        function onInitialStatusBlur() {
            errors.initialStatus = form.initialStatus
                ? ""
                : t("finance.donations.validation.statusRequired");
        }
        watch(() => form.donationType, (value) => {
            if (value)
                errors.donationType = "";
        });
        function onDonationTypeBlur() {
            errors.donationType = form.donationType
                ? ""
                : t("finance.donations.validation.typeRequired");
        }
        watch(() => form.donationForm, (value) => {
            if (value)
                errors.donationForm = "";
        });
        function onDonationFormBlur() {
            errors.donationForm = form.donationForm
                ? ""
                : t("finance.donations.validation.donationFormRequired");
        }
        watch(() => form.subscriptionId, (value) => {
            if (value.trim())
                errors.subscriptionId = "";
        });
        function onSubscriptionIdBlur() {
            if (form.donationType !== "Regular")
                return;
            errors.subscriptionId = form.subscriptionId.trim()
                ? ""
                : t("finance.donations.validation.subscriptionRequired");
        }
        function resetForm() {
            Object.assign(form, FORM_DEFAULTS, { donationDate: todayLocalIsoDate() });
            errors.contact = "";
            errors.organizationId = "";
            errors.amount = "";
            errors.campaign = "";
            errors.donationDate = "";
            errors.initialStatus = "";
            errors.donationType = "";
            errors.subscriptionId = "";
            errors.chequeNumber = "";
            errors.paymentMethod = "";
            errors.donationNature = "";
            errors.donationForm = "";
            errors.affectation = "";
            affectationEverHadValue.value = false;
        }
        // ─── Validation errors ────────────────────────────────────────────────────────
        const errors = reactive({
            contact: "",
            organizationId: "",
            amount: "",
            campaign: "",
            donationDate: "",
            initialStatus: "",
            donationType: "",
            subscriptionId: "",
            chequeNumber: "",
            paymentMethod: "",
            donationNature: "",
            donationForm: "",
            affectation: "",
        });
        // ─── Validation ───────────────────────────────────────────────────────────────
        // Pure predicate driving the submit button's disabled state — mirrors the
        // required-field rules in `validate()` below WITHOUT its error-message side
        // effects (we must not paint every field red the instant the modal opens).
        // Keep this in lock-step with `validate()`: any rule added there that the user
        // can satisfy from the form must be reflected here, or the CTA enables on an
        // invalid form. `validate()` stays the authoritative gate at submit time.
        const isFormValid = computed(() => {
            // Donor — a contact is mandatory for both types (OP-5843); an Organisation donation also
            // requires the organisation itself.
            if (!form.contact.trim())
                return false;
            if (form.donorType === "Organization" && !form.organizationId.trim())
                return false;
            // OP-7954: the donor must carry everything the receipt needs. "Not checked yet" and "could not
            // check" both count as not complete — the save would be refused anyway.
            if (!receiptInformation.isComplete.value)
                return false;
            if (!form.campaign.trim())
                return false;
            const amount = parseFloat(form.amountStr);
            if (!form.amountStr || isNaN(amount) || amount <= 0)
                return false;
            if (!workspaceCurrency.value)
                return false;
            if (!form.donationDate)
                return false;
            if (!form.donationType)
                return false;
            if (form.donationType === "Regular" && !form.subscriptionId.trim())
                return false;
            if (!form.donationNature)
                return false;
            // PaymentMethod (and cheque number) only gate when nature = Cash.
            if (form.donationNature === "Cash") {
                if (!form.paymentMethod)
                    return false;
                if (form.paymentMethod === "Cheque" && !form.chequeNumber.trim())
                    return false;
            }
            if (!form.donationForm)
                return false;
            if (!form.initialStatus)
                return false;
            // Allocation (Affectation) is mandatory per OP-5841.
            if (!form.affectation.trim())
                return false;
            return true;
        });
        function validate() {
            let valid = true;
            errors.contact = "";
            errors.organizationId = "";
            errors.amount = "";
            errors.campaign = "";
            errors.donationDate = "";
            errors.initialStatus = "";
            errors.donationType = "";
            errors.subscriptionId = "";
            errors.chequeNumber = "";
            errors.paymentMethod = "";
            errors.donationNature = "";
            errors.donationForm = "";
            errors.affectation = "";
            // Organisation donations also require the organisation itself.
            if (form.donorType === "Organization" && !form.organizationId.trim()) {
                errors.organizationId = t("finance.donations.validation.donorRequired");
                valid = false;
            }
            // A contact is mandatory for both donor types (OP-5843).
            if (!form.contact.trim()) {
                errors.contact = t("finance.donations.validation.contactRequired");
                valid = false;
            }
            // Campagne is required per V2 spec.
            if (!form.campaign.trim()) {
                errors.campaign = t("finance.donations.validation.campaignRequired");
                valid = false;
            }
            const amount = parseFloat(form.amountStr);
            if (!form.amountStr || isNaN(amount) || amount <= 0) {
                errors.amount = t("finance.donations.validation.amountPositive");
                valid = false;
            }
            if (!form.donationDate) {
                errors.donationDate = t("finance.donations.validation.dateRequired");
                valid = false;
            }
            if (!form.donationType) {
                errors.donationType = t("finance.donations.validation.typeRequired");
                valid = false;
            }
            if (form.donationType === "Regular" && !form.subscriptionId.trim()) {
                errors.subscriptionId = t("finance.donations.validation.subscriptionRequired");
                valid = false;
            }
            if (!form.donationNature) {
                errors.donationNature = t("finance.donations.validation.natureRequired");
                valid = false;
            }
            // PaymentMethod is required only when DonationNature = Cash (Numéraire).
            if (form.donationNature === "Cash" && !form.paymentMethod) {
                errors.paymentMethod = t("finance.donations.validation.paymentMethodRequired");
                valid = false;
            }
            if (form.donationNature === "Cash" &&
                form.paymentMethod === "Cheque" &&
                !form.chequeNumber.trim()) {
                errors.chequeNumber = t("finance.donations.validation.chequeNumberRequired");
                valid = false;
            }
            // Forme du don - Cerfa 11580 is required per V2 spec.
            if (!form.donationForm) {
                errors.donationForm = t("finance.donations.validation.donationFormRequired");
                valid = false;
            }
            // Statut is required (defaulted to Recorded; guard against empty selection).
            if (!form.initialStatus) {
                errors.initialStatus = t("finance.donations.validation.statusRequired");
                valid = false;
            }
            // Allocation (Affectation) is mandatory per OP-5841.
            if (!form.affectation.trim()) {
                errors.affectation = t("finance.donations.validation.allocationRequired");
                valid = false;
            }
            return valid;
        }
        // ─── Handlers ─────────────────────────────────────────────────────────────────
        function handleCancel() {
            resetForm();
            open.value = false;
            emit("close");
        }
        async function handleSubmit() {
            if (!validate())
                return;
            const currency = workspaceCurrency.value;
            if (!currency)
                return;
            const campaignMatch = form.campaign
                ? campaigns.value.find((c) => c.id === form.campaign)
                : undefined;
            // Frequency comes from the selected subscription — never from user input — to keep
            // the donation and its parent subscription in lock-step.
            const subscriptionMatch = form.subscriptionId
                ? subscriptions.value.find((s) => s.id === form.subscriptionId)
                : undefined;
            // Donor display name fallback (see CreateDonationPayload.donorDisplayName). The picker
            // already resolved the name, so forward it for OPDON to snapshot when its own OPUS
            // snapshot has not yet caught up with a donor created moments earlier. We send the
            // name only (never a GUID) — undefined when no real name is available.
            const contactMatch = form.contact
                ? contacts.value.find((c) => c.id === form.contact)
                : undefined;
            const organisationMatch = form.donorType === "Organization" && form.organizationId
                ? organisations.value.find((o) => o.id === form.organizationId)
                : undefined;
            // An org created when more than the picker's 50-org buffer exists is absent from
            // organisations.value, but onOrganisationCreated seeds its name into the suggestion list.
            // Fall back to that label so the donor name is still forwarded; guard against the picker's
            // GUID fallback (organisationToOption uses the id when no name) so we never send an id.
            const organisationSuggestionLabel = form.donorType === "Organization" && form.organizationId
                ? organisationSuggestions.value.find((o) => o.value === form.organizationId)?.label
                : undefined;
            // Contact (donor for Individual / referent for Organisation) name. Resolve from
            // contacts.value when present, else fall back to the picker suggestion label: the
            // contact-locked fiche path seeds only contactSuggestions (not contacts.value), so
            // without this fallback a donation created from a contact fiche would forward no
            // donor name (parity with SubscriptionCreateModal). Guard against the picker's GUID
            // fallback so we never send an id.
            const contactSuggestionLabel = form.contact
                ? contactSuggestions.value.find((o) => o.value === form.contact)?.label
                : undefined;
            const contactName = (contactMatch ? `${contactMatch.firstName} ${contactMatch.lastName}`.trim() : "") ||
                (contactSuggestionLabel && contactSuggestionLabel !== form.contact
                    ? contactSuggestionLabel
                    : "") ||
                undefined;
            const donorDisplayName = form.donorType === "Organization"
                ? organisationMatch?.mainInformation.legalName?.trim() ||
                    organisationMatch?.mainInformation.acronym?.trim() ||
                    (organisationSuggestionLabel !== form.organizationId
                        ? organisationSuggestionLabel
                        : undefined) ||
                    undefined
                : contactName;
            // Referent contact name (OP-5843) — sent for an Organisation donation so OPDON can snapshot
            // the "Contact référent" name while its OPUS contact snapshot catches up. The donor name above
            // is the org's; this is the person's.
            const contactDisplayName = contactName;
            // Mode de paiement only travels on the wire when nature = Cash.
            const includePaymentMethod = form.donationNature === "Cash" && !!form.paymentMethod;
            try {
                const donation = await store.createDonation({
                    donationType: form.donationType,
                    totalAmount: parseFloat(form.amountStr),
                    currency,
                    donationNature: form.donationNature,
                    // Modale Don V2: replaces the former `isPromise` boolean — Promesse is now a
                    // first-class TransactionStatus value selected directly in the Statut dropdown.
                    initialStatus: form.initialStatus,
                    donorType: form.donorType,
                    // BFF column is `timestamp with time zone` (Npgsql requires UTC Kind, so we send a Z suffix).
                    // Anchor the calendar day at 12:00 UTC so viewers in any reasonable timezone see the same date
                    // the user picked — midnight UTC would shift the date by a day for negative offsets.
                    donationDate: `${form.donationDate}T12:00:00Z`,
                    ...(includePaymentMethod ? { paymentMethod: form.paymentMethod } : {}),
                    // contactId is the contact to link, sent for BOTH donor types (OP-5843): for an Individual
                    // donation it identifies the donor; for an Organisation donation it is the mandatory
                    // "Contact référent" stored on the donation (the donor is the org — organizationId below).
                    // OPDON keeps the referent out of the donor-resolution cascade for orgs, so it no longer
                    // creates an Individual donor / drops the organisation.
                    ...(form.contact.trim() ? { contactId: form.contact.trim() } : {}),
                    ...(form.donorType === "Organization" && form.organizationId
                        ? { organizationId: form.organizationId }
                        : {}),
                    ...(donorDisplayName ? { donorDisplayName } : {}),
                    // Referent contact name only matters for an Organisation donation (the org name is the
                    // donorDisplayName); for an Individual the donor name already carries it.
                    ...(form.donorType === "Organization" && contactDisplayName
                        ? { contactDisplayName }
                        : {}),
                    ...(form.donationType === "Regular" && subscriptionMatch
                        ? {
                            subscriptionId: subscriptionMatch.id,
                            donationFrequency: subscriptionMatch.frequency,
                        }
                        : {}),
                    ...(form.donationNature === "Cash" && form.paymentMethod === "Cheque" && form.chequeNumber
                        ? { chequeNumber: form.chequeNumber }
                        : {}),
                    // A transaction reference typed in this modal is always a manually-recorded
                    // back-office entry, so it must travel with its provider — OPDON rejects an
                    // externalTransactionId without an externalTransactionProvider (OP-5823).
                    ...(form.donationNature === "Cash" && form.externalTransactionId.trim()
                        ? {
                            externalTransactionId: form.externalTransactionId.trim(),
                            externalTransactionProvider: "Manual",
                        }
                        : {}),
                    // AutoComplete clears its modelValue on input, so the raw typed text is never
                    // exposed here — this guard keeps things explicit and skips campaignId when no
                    // suggestion was selected.
                    ...(campaignMatch
                        ? {
                            campaignId: campaignMatch.id,
                            ...(campaignMatch.name ? { campaignName: campaignMatch.name } : {}),
                        }
                        : {}),
                    // Affectation: the selected project becomes the donation's 100% allocation at
                    // creation (OPDON redirects its auto-allocation to this project instead of the
                    // tenant default). form.affectation holds the selected project id.
                    ...(form.affectation.trim() ? { projectId: form.affectation.trim() } : {}),
                    ...(form.donationForm ? { donationForm: form.donationForm } : {}),
                    ...(form.donationSource ? { donationSource: form.donationSource } : {}),
                });
                toastSuccess("finance.donations.saveSuccess");
                resetForm();
                emit("created", donation.id);
                if (props.lockedDonor) {
                    // Opened from a fiche: stay on the fiche. The parent tab's `@created` handler
                    // refetches the list in place; just close the modal. There is no list-view
                    // URL-sync on a fiche, so flipping `open` here is safe (unlike the global flow).
                    open.value = false;
                }
                else {
                    // Global flow: navigate to the new donation's detail page. We deliberately do
                    // NOT set `open.value = false` first: that flips the list view's
                    // `showCreateModal`, whose URL-sync watcher fires a competing
                    // `router.replace({ query })` in the same tick that cancels this push and
                    // strands the user on the list. The route change to DonationDetail unmounts the
                    // list view (and this modal) on its own, so the modal closes without the race.
                    await router.push({ name: "DonationDetail", params: { donationId: donation.id } });
                }
            }
            catch (e) {
                // The pre-check runs on donor selection, but the record can change between that answer and this
                // save — or the DonorId path can resolve links the probe never saw. The backend then refuses
                // with the field list; showing the generic save error here would hide the one thing the user
                // needs to act on, and the detail view already names them (R71).
                const gaps = parseDonorInformationGaps(getApiErrorResult(e));
                if (gaps.length > 0) {
                    toastError("finance.donations.saveDonorInformationMissing", {
                        details: gaps.map((gap) => describeDonorInformationGap(gap, t)).join(" · "),
                    });
                    return;
                }
                if (getApiErrorCode(e) === "Workspace.Currency.NotAvailable") {
                    toastError("finance.donations.errors.workspaceCurrencyUnavailable");
                    return;
                }
                toastError("finance.donations.saveError");
            }
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createVNode(_unref(Modal), {
                    modelValue: open.value,
                    "onUpdate:modelValue": [
                        _cache[17] || (_cache[17] = ($event) => ((open).value = $event)),
                        _cache[18] || (_cache[18] = (v) => !v && handleCancel())
                    ],
                    size: "lg",
                    "footer-position": "split",
                    "header-position": "center",
                    "show-close-button": !_unref(store).isSaving,
                    "show-header-border": false,
                    "show-footer-border": false,
                    "header-class": "pt-2!",
                    dismissable: !_unref(store).isSaving,
                    "close-on-escape": !_unref(store).isSaving,
                    "aria-labelledby": _unref(titleId),
                    "aria-describedby": _unref(descriptionId)
                }, {
                    header: _withCtx(() => [
                        _createElementVNode("div", _hoisted_1, [
                            _createElementVNode("span", {
                                id: _unref(titleId),
                                class: "text-xl font-bold text-(--text-text-black-default) dark:text-(--text-text-white-default)"
                            }, _toDisplayString(_ctx.$t("finance.donations.create.title")), 9 /* TEXT, PROPS */, _hoisted_2),
                            _createElementVNode("span", {
                                id: _unref(descriptionId),
                                class: "text-sm font-normal leading-5 text-(--text-text-heading) dark:text-(--text-text-white-default)"
                            }, _toDisplayString(_ctx.$t("finance.donations.create.subtitle")), 9 /* TEXT, PROPS */, _hoisted_3)
                        ])
                    ]),
                    footer: _withCtx(() => [
                        _createVNode(_unref(Button), {
                            variant: "secondary",
                            label: _ctx.$t('common.cancel'),
                            disabled: _unref(store).isSaving,
                            onClick: handleCancel
                        }, null, 8 /* PROPS */, ["label", "disabled"]),
                        _createVNode(_unref(Button), {
                            variant: "primary",
                            "icon-left": "ri-check-line",
                            label: _ctx.$t('finance.donations.create.submit'),
                            disabled: !isFormValid.value || _unref(store).isSaving,
                            loading: _unref(store).isSaving,
                            "data-testid": "donation-create-submit",
                            onClick: handleSubmit
                        }, null, 8 /* PROPS */, ["label", "disabled", "loading"])
                    ]),
                    default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_4, [
                            _createVNode(DonorReceiptInformationNotice, {
                                gaps: _unref(receiptInformation).issuerGaps.value,
                                unavailable: false,
                                "heading-key": "finance.donations.create.receiptInformation.workspaceTitle"
                            }, null, 8 /* PROPS */, ["gaps"]),
                            _createElementVNode("div", _hoisted_5, [
                                _createElementVNode("div", _hoisted_6, [
                                    _createElementVNode("h3", _hoisted_7, _toDisplayString(_ctx.$t("finance.donations.create.sectionDonor")), 1 /* TEXT */),
                                    _createVNode(_unref(SelectInput), {
                                        modelValue: form.donorType,
                                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((form.donorType) = $event)),
                                        label: _ctx.$t('finance.donations.fields.donorType'),
                                        options: _unref(donorTypeOptions),
                                        required: true,
                                        disabled: !!props.lockedDonor,
                                        "full-width": ""
                                    }, null, 8 /* PROPS */, ["modelValue", "label", "options", "disabled"]),
                                    (form.donorType === 'Organization')
                                        ? (_openBlock(), _createBlock(_unref(AutoComplete), _mergeProps({ key: 0 }, _unref(buildAutoCompleteLabels)(_unref(t)), {
                                            modelValue: form.organizationId,
                                            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((form.organizationId) = $event)),
                                            label: _ctx.$t('finance.donations.fields.orgName'),
                                            placeholder: _ctx.$t('finance.donations.fields.orgNamePlaceholder'),
                                            options: organisationSuggestions.value,
                                            loading: organisationsLoading.value,
                                            error: errors.organizationId,
                                            required: true,
                                            disabled: props.lockedDonor?.donorType === 'Organization',
                                            "icon-left": '',
                                            "icon-right": '',
                                            "data-testid": "donation-create-org-picker",
                                            "full-width": "",
                                            "see-all-label": organisationSeeAllLabel.value,
                                            "create-label": organisationCreateLabel,
                                            onSearch: onOrganisationSearch,
                                            onBlur: onOrganizationBlur,
                                            onSeeAll: openAllOrgsModal,
                                            onCreate: openCreateOrg
                                        }), null, 16 /* FULL_PROPS */, ["modelValue", "label", "placeholder", "options", "loading", "error", "disabled", "see-all-label"]))
                                        : _createCommentVNode("v-if", true),
                                    _createVNode(_unref(AutoComplete), _mergeProps(_unref(buildAutoCompleteLabels)(_unref(t)), {
                                        modelValue: form.contact,
                                        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((form.contact) = $event)),
                                        label: _ctx.$t('finance.donations.fields.contactName'),
                                        placeholder: _ctx.$t('finance.donations.fields.contactNamePlaceholder'),
                                        options: contactSuggestions.value,
                                        loading: contactsLoading.value,
                                        error: errors.contact,
                                        required: true,
                                        disabled: props.lockedDonor?.donorType === 'Individual',
                                        "icon-left": '',
                                        "icon-right": '',
                                        "data-testid": "donation-create-contact-picker",
                                        "full-width": "",
                                        "see-all-label": contactSeeAllLabel.value,
                                        "create-label": contactCreateLabel,
                                        onSearch: onContactSearch,
                                        onBlur: onContactBlur,
                                        onSeeAll: openAllContactsModal,
                                        onCreate: _cache[3] || (_cache[3] = ($event) => (openCreateContact()))
                                    }), null, 16 /* FULL_PROPS */, ["modelValue", "label", "placeholder", "options", "loading", "error", "disabled", "see-all-label"]),
                                    _createVNode(DonorReceiptInformationNotice, {
                                        class: "mb-5",
                                        gaps: _unref(receiptInformation).donorGaps.value,
                                        unavailable: _unref(receiptInformation).unavailable.value,
                                        "contact-id": form.contact || undefined,
                                        "organization-id": form.donorType === 'Organization' ? form.organizationId || undefined : undefined,
                                        "on-retry": _unref(receiptInformation).retry
                                    }, null, 8 /* PROPS */, ["gaps", "unavailable", "contact-id", "organization-id", "on-retry"]),
                                    _createVNode(_unref(AutoComplete), _mergeProps(_unref(buildAutoCompleteLabels)(_unref(t)), {
                                        modelValue: form.campaign,
                                        "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => ((form.campaign) = $event)),
                                        label: _ctx.$t('finance.donations.fields.campaign'),
                                        placeholder: _ctx.$t('finance.donations.fields.campaignPlaceholder'),
                                        options: _unref(campaignSuggestions),
                                        loading: _unref(campaignsLoading),
                                        error: errors.campaign,
                                        required: true,
                                        "icon-left": '',
                                        "icon-right": '',
                                        "data-testid": "donation-create-campaign-picker",
                                        "full-width": "",
                                        "see-all-label": campaignSeeAllLabel.value,
                                        onSearch: _unref(onCampaignSearch),
                                        onBlur: onCampaignBlur,
                                        onSeeAll: openAllCampaignsModal
                                    }), null, 16 /* FULL_PROPS */, ["modelValue", "label", "placeholder", "options", "loading", "error", "see-all-label", "onSearch"])
                                ]),
                                _createElementVNode("div", _hoisted_8, [
                                    _createElementVNode("h3", _hoisted_9, _toDisplayString(_ctx.$t("finance.donations.create.sectionPayment")), 1 /* TEXT */),
                                    _createVNode(_unref(SelectInput), {
                                        modelValue: form.donationNature,
                                        "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event) => ((form.donationNature) = $event)),
                                        label: _ctx.$t('finance.donations.fields.donationNature'),
                                        options: _unref(donationNatureOptions),
                                        error: errors.donationNature,
                                        required: true,
                                        "data-testid": "donation-create-donation-nature",
                                        "full-width": "",
                                        onBlur: onDonationNatureBlur
                                    }, null, 8 /* PROPS */, ["modelValue", "label", "options", "error"]),
                                    _createVNode(_unref(NumberInput), {
                                        modelValue: form.amountStr,
                                        "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event) => ((form.amountStr) = $event)),
                                        label: _ctx.$t('finance.donations.fields.totalAmount'),
                                        placeholder: _ctx.$t('finance.donations.fields.amountPlaceholder'),
                                        hint: _ctx.$t('finance.donations.fields.amountHint'),
                                        error: _unref(workspaceCurrencyUnavailable)
                                            ? _ctx.$t('finance.donations.errors.workspaceCurrencyUnavailable')
                                            : errors.amount,
                                        required: true,
                                        prefix: amountPrefix.value,
                                        disabled: !_unref(workspaceCurrency),
                                        min: 0,
                                        step: 0.01,
                                        "data-testid": "donation-create-amount",
                                        "full-width": "",
                                        onBlur: onAmountBlur
                                    }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder", "hint", "error", "prefix", "disabled"]),
                                    (form.donationNature === 'Cash')
                                        ? (_openBlock(), _createBlock(_unref(SelectInput), {
                                            key: 0,
                                            modelValue: form.paymentMethod,
                                            "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event) => ((form.paymentMethod) = $event)),
                                            label: _ctx.$t('finance.donations.fields.paymentMethod'),
                                            placeholder: _ctx.$t('finance.donations.fields.paymentMethodPlaceholder'),
                                            options: _unref(paymentMethodOptions),
                                            error: errors.paymentMethod,
                                            required: true,
                                            "data-testid": "donation-create-payment-method",
                                            "full-width": "",
                                            onBlur: onPaymentMethodBlur
                                        }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder", "options", "error"]))
                                        : _createCommentVNode("v-if", true),
                                    (form.donationNature === 'Cash' && form.paymentMethod === 'Cheque')
                                        ? (_openBlock(), _createBlock(_unref(TextInput), {
                                            key: 1,
                                            modelValue: form.chequeNumber,
                                            "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event) => ((form.chequeNumber) = $event)),
                                            label: _ctx.$t('finance.donations.fields.chequeNumber'),
                                            error: errors.chequeNumber,
                                            required: true,
                                            "data-testid": "donation-create-cheque-number",
                                            "full-width": "",
                                            onBlur: onChequeNumberBlur
                                        }, null, 8 /* PROPS */, ["modelValue", "label", "error"]))
                                        : _createCommentVNode("v-if", true),
                                    (form.donationNature === 'Cash')
                                        ? (_openBlock(), _createBlock(_unref(TextInput), {
                                            key: 2,
                                            modelValue: form.externalTransactionId,
                                            "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event) => ((form.externalTransactionId) = $event)),
                                            label: _ctx.$t('finance.donations.fields.externalTransactionId'),
                                            placeholder: _ctx.$t('finance.donations.fields.externalTransactionIdPlaceholder'),
                                            "full-width": ""
                                        }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder"]))
                                        : _createCommentVNode("v-if", true)
                                ])
                            ]),
                            _createElementVNode("div", _hoisted_10, [
                                _createVNode(_unref(DatePickerInput), {
                                    modelValue: _unref(donationDateDisplay),
                                    "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event) => (_isRef(donationDateDisplay) ? (donationDateDisplay).value = $event : null)),
                                    label: _ctx.$t('finance.donations.fields.donationDate'),
                                    error: errors.donationDate,
                                    required: true,
                                    format: dateFormat.value,
                                    language: _unref(locale),
                                    "week-start": 1,
                                    "full-width": ""
                                }, null, 8 /* PROPS */, ["modelValue", "label", "error", "format", "language"]),
                                _createVNode(_unref(SelectInput), {
                                    modelValue: form.initialStatus,
                                    "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event) => ((form.initialStatus) = $event)),
                                    label: _ctx.$t('finance.donations.fields.status'),
                                    placeholder: _ctx.$t('finance.donations.fields.statusPlaceholder'),
                                    options: creationStatusOptions.value,
                                    error: errors.initialStatus,
                                    required: true,
                                    "full-width": "",
                                    onBlur: onInitialStatusBlur
                                }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder", "options", "error"]),
                                _createVNode(_unref(SelectInput), {
                                    modelValue: form.donationType,
                                    "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event) => ((form.donationType) = $event)),
                                    label: _ctx.$t('finance.donations.fields.donationType'),
                                    options: _unref(donationTypeOptions),
                                    error: errors.donationType,
                                    required: true,
                                    "full-width": "",
                                    onBlur: onDonationTypeBlur
                                }, null, 8 /* PROPS */, ["modelValue", "label", "options", "error"]),
                                _createVNode(_unref(AutoComplete), _mergeProps(_unref(buildAutoCompleteLabels)(_unref(t)), {
                                    modelValue: form.affectation,
                                    "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event) => ((form.affectation) = $event)),
                                    label: _ctx.$t('finance.donations.fields.affectation'),
                                    placeholder: _ctx.$t('finance.donations.fields.affectationPlaceholder'),
                                    options: projectSuggestions.value,
                                    loading: projectsLoading.value,
                                    "empty-label": _ctx.$t('finance.donations.detail.alloc.projectSearchEmpty'),
                                    error: errors.affectation,
                                    required: true,
                                    "icon-left": '',
                                    "icon-right": "ri-search-line",
                                    "full-width": "",
                                    "see-all-label": projectSeeAllLabel.value,
                                    onSearch: onProjectSearch,
                                    onBlur: onAffectationBlur,
                                    onSeeAll: openAllProjectsModal
                                }), null, 16 /* FULL_PROPS */, ["modelValue", "label", "placeholder", "options", "loading", "empty-label", "error", "see-all-label"]),
                                _createVNode(_unref(SelectInput), {
                                    modelValue: form.donationForm,
                                    "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event) => ((form.donationForm) = $event)),
                                    label: _ctx.$t('finance.donations.fields.donationForm'),
                                    options: _unref(donationFormOptions),
                                    error: errors.donationForm,
                                    required: true,
                                    "full-width": "",
                                    onBlur: onDonationFormBlur
                                }, null, 8 /* PROPS */, ["modelValue", "label", "options", "error"]),
                                _createVNode(_unref(SelectInput), {
                                    modelValue: form.donationSource,
                                    "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event) => ((form.donationSource) = $event)),
                                    label: _ctx.$t('finance.donations.fields.donationSource'),
                                    options: _unref(donationSourceOptions),
                                    "full-width": ""
                                }, null, 8 /* PROPS */, ["modelValue", "label", "options"]),
                                (form.donationType === 'Regular')
                                    ? (_openBlock(), _createBlock(_unref(AutoComplete), _mergeProps({ key: 0 }, _unref(buildAutoCompleteLabels)(_unref(t)), {
                                        modelValue: form.subscriptionId,
                                        "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event) => ((form.subscriptionId) = $event)),
                                        label: _ctx.$t('finance.donations.fields.subscriptionId'),
                                        placeholder: _ctx.$t('finance.donations.fields.subscriptionIdPlaceholder'),
                                        options: _unref(subscriptionSuggestions),
                                        loading: _unref(subscriptionsLoading),
                                        error: errors.subscriptionId,
                                        required: true,
                                        "icon-left": '',
                                        "icon-right": '',
                                        "full-width": "",
                                        onSearch: _unref(searchSubscriptions),
                                        onBlur: onSubscriptionIdBlur
                                    }), null, 16 /* FULL_PROPS */, ["modelValue", "label", "placeholder", "options", "loading", "error", "onSearch"]))
                                    : _createCommentVNode("v-if", true)
                            ])
                        ])
                    ]),
                    _: 1 /* STABLE */
                }, 8 /* PROPS */, ["modelValue", "show-close-button", "dismissable", "close-on-escape", "aria-labelledby", "aria-describedby"]),
                (createContactModalMounted.value)
                    ? (_openBlock(), _createBlock(CreateContactModal, {
                        key: 0,
                        modelValue: showCreateContactModal.value,
                        "onUpdate:modelValue": _cache[19] || (_cache[19] = ($event) => ((showCreateContactModal).value = $event)),
                        "redirect-on-create": false,
                        "initial-organisation-id": contactModalOrgId.value,
                        "initial-organisation-label": contactModalOrgLabel.value,
                        "force-professional-type": form.donorType === 'Organization',
                        onCreated: onContactCreated
                    }, null, 8 /* PROPS */, ["modelValue", "initial-organisation-id", "initial-organisation-label", "force-professional-type"]))
                    : _createCommentVNode("v-if", true),
                (createOrgModalMounted.value)
                    ? (_openBlock(), _createBlock(CreateOrganisationModal, {
                        key: 1,
                        modelValue: showCreateOrgModal.value,
                        "onUpdate:modelValue": _cache[20] || (_cache[20] = ($event) => ((showCreateOrgModal).value = $event)),
                        "redirect-on-create": false,
                        "initial-name": createOrgInitialName.value,
                        onCreated: onOrganisationCreated
                    }, null, 8 /* PROPS */, ["modelValue", "initial-name"]))
                    : _createCommentVNode("v-if", true),
                _createVNode(_unref(AutoCompleteAllResultsModal), {
                    modelValue: showAllOrgsModal.value,
                    "onUpdate:modelValue": _cache[21] || (_cache[21] = ($event) => ((showAllOrgsModal).value = $event)),
                    options: allOrgsOptions.value,
                    loading: allOrgsLoading.value,
                    error: allOrgsError.value,
                    title: _ctx.$t('finance.donations.create.allResults.title', {
                        count: allOrgsOptions.value.length,
                        query: allOrgsQuery.value,
                    }),
                    subtitle: _ctx.$t('finance.donations.create.allResults.subtitle'),
                    "close-label": _ctx.$t('finance.donations.create.allResults.close'),
                    "select-label": _ctx.$t('finance.donations.create.allResults.select'),
                    "load-error-label": _ctx.$t('finance.donations.create.allResults.loadError'),
                    "empty-label": _ctx.$t('finance.donations.create.allResults.empty'),
                    onConfirm: onAllOrgsConfirm
                }, null, 8 /* PROPS */, ["modelValue", "options", "loading", "error", "title", "subtitle", "close-label", "select-label", "load-error-label", "empty-label"]),
                _createVNode(_unref(AutoCompleteAllResultsModal), {
                    modelValue: showAllProjectsModal.value,
                    "onUpdate:modelValue": _cache[22] || (_cache[22] = ($event) => ((showAllProjectsModal).value = $event)),
                    options: allProjectsOptions.value,
                    loading: allProjectsLoading.value,
                    error: allProjectsError.value,
                    title: _ctx.$t('finance.donations.create.allResults.title', {
                        count: allProjectsOptions.value.length,
                        query: allProjectsQuery.value,
                    }),
                    subtitle: _ctx.$t('finance.donations.create.allResults.subtitle'),
                    "close-label": _ctx.$t('finance.donations.create.allResults.close'),
                    "select-label": _ctx.$t('finance.donations.create.allResults.select'),
                    "load-error-label": _ctx.$t('finance.donations.create.allResults.loadError'),
                    "empty-label": _ctx.$t('finance.donations.create.allResults.empty'),
                    onConfirm: onAllProjectsConfirm
                }, null, 8 /* PROPS */, ["modelValue", "options", "loading", "error", "title", "subtitle", "close-label", "select-label", "load-error-label", "empty-label"]),
                _createVNode(_unref(AutoCompleteAllResultsModal), {
                    modelValue: showAllContactsModal.value,
                    "onUpdate:modelValue": _cache[23] || (_cache[23] = ($event) => ((showAllContactsModal).value = $event)),
                    options: allContactsOptions.value,
                    loading: allContactsLoading.value,
                    error: allContactsError.value,
                    title: _ctx.$t('finance.donations.create.allResults.title', {
                        count: allContactsOptions.value.length,
                        query: allContactsQuery.value,
                    }),
                    subtitle: _ctx.$t('finance.donations.create.allResults.subtitle'),
                    "close-label": _ctx.$t('finance.donations.create.allResults.close'),
                    "select-label": _ctx.$t('finance.donations.create.allResults.select'),
                    "load-error-label": _ctx.$t('finance.donations.create.allResults.loadError'),
                    "empty-label": _ctx.$t('finance.donations.create.allResults.empty'),
                    onConfirm: onAllContactsConfirm
                }, null, 8 /* PROPS */, ["modelValue", "options", "loading", "error", "title", "subtitle", "close-label", "select-label", "load-error-label", "empty-label"]),
                _createVNode(_unref(AutoCompleteAllResultsModal), {
                    modelValue: showAllCampaignsModal.value,
                    "onUpdate:modelValue": _cache[24] || (_cache[24] = ($event) => ((showAllCampaignsModal).value = $event)),
                    options: allCampaignsOptions.value,
                    loading: allCampaignsLoading.value,
                    error: allCampaignsError.value,
                    title: _ctx.$t('finance.donations.create.allResults.title', {
                        count: allCampaignsTotal.value,
                        query: allCampaignsQuery.value,
                    }),
                    subtitle: _ctx.$t('finance.donations.create.allResults.subtitle'),
                    "close-label": _ctx.$t('finance.donations.create.allResults.close'),
                    "select-label": _ctx.$t('finance.donations.create.allResults.select'),
                    "load-error-label": _ctx.$t('finance.donations.create.allResults.loadError'),
                    "empty-label": _ctx.$t('finance.donations.create.allResults.empty'),
                    onConfirm: onAllCampaignsConfirm
                }, null, 8 /* PROPS */, ["modelValue", "options", "loading", "error", "title", "subtitle", "close-label", "select-label", "load-error-label", "empty-label"])
            ], 64 /* STABLE_FRAGMENT */));
        };
    }
});
